import React, { useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { playTweet } from "../lib/birdSounds";

// Sprite variants — same illustration with different blue tones (cream/peach/gray unchanged)
const VARIANTS = [
  { open: "/bird-sprite.png",    closed: "/bird-closed.png"    }, // original slate
  { open: "/bird-sprite-v1.png", closed: "/bird-closed-v1.png" }, // cyan-teal
  { open: "/bird-sprite-v2.png", closed: "/bird-closed-v2.png" }, // indigo
  { open: "/bird-sprite-v3.png", closed: "/bird-closed-v3.png" }, // sea-green-blue
  { open: "/bird-sprite-v4.png", closed: "/bird-closed-v4.png" }, // lavender
  { open: "/bird-sprite-v5.png", closed: "/bird-closed-v5.png" }, // dusty navy
];

function Bird({
  size,
  baseTopVh,
  baseLeftPct,
  range,
  duration,
  delay,
  flapSpeed,
  flapPhase,
  bodyTilt,
  direction,
  variant,
  parallaxFactor,
  scrollY,
}) {
  const mirror = direction < 0 ? "scaleX(-1)" : "";
  const sprite = VARIANTS[variant % VARIANTS.length];

  // Parallax: smaller birds drift slower than scroll, giving sense of depth
  const parallaxY = useTransform(scrollY, (y) => y * parallaxFactor);

  // Negative animation-delay desyncs the flap so birds don't all flap in unison
  const flapDelayCss = `${-flapPhase}ms`;

  return (
    <motion.div
      style={{
        position: "absolute",
        top: `${baseTopVh}vh`,
        left: `${baseLeftPct}%`,
        pointerEvents: "none",
        willChange: "transform",
        y: parallaxY,
      }}
    >
      <motion.div
        animate={{
          x: range.x,
          y: range.y,
          rotate: range.r,
          opacity: range.opacity,
        }}
        transition={{
          duration,
          delay,
          repeat: Infinity,
          ease: "easeInOut",
          times: range.times,
        }}
      >
        <div
          style={{
            width: `${size}px`,
            height: `${size}px`,
            transform: `${mirror} rotateZ(${bodyTilt}deg)`,
            filter: "drop-shadow(0 2px 3px rgba(60,70,90,0.18))",
            position: "relative",
          }}
        >
          {/* Open wings — visible during first half of flap cycle */}
          <img
            src={sprite.open}
            alt=""
            draggable={false}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              userSelect: "none",
              display: "block",
              animation: `bird-show-open ${flapSpeed}ms steps(1, end) infinite`,
              animationDelay: flapDelayCss,
            }}
          />
          {/* Closed wings — visible during second half of flap cycle */}
          <img
            src={sprite.closed}
            alt=""
            draggable={false}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              userSelect: "none",
              display: "block",
              animation: `bird-show-closed ${flapSpeed}ms steps(1, end) infinite`,
              animationDelay: flapDelayCss,
            }}
          />
        </div>
      </motion.div>
    </motion.div>
  );
}

function makeRange(seed) {
  const r = (n) => ((Math.sin(seed * 9.7 + n * 2.3) + 1) / 2) * 2 - 1;
  return {
    x: [0, 60 * r(1), -40 * r(2), 70 * r(3), -50 * r(4), 30 * r(5), 0],
    y: [0, -30 * r(6), 25 * r(7), -20 * r(8), 35 * r(9), -15 * r(10), 0],
    r: [0, 8 * r(11), -6 * r(12), 10 * r(13), -4 * r(14), 6 * r(15), 0],
    opacity: [0.85, 1, 0.95, 1, 0.92, 1, 0.85],
    times: [0, 0.18, 0.35, 0.55, 0.72, 0.88, 1],
  };
}

function makeLoopRange(seed) {
  const r = (n) => ((Math.sin(seed * 11.3 + n * 1.7) + 1) / 2) * 2 - 1;
  return {
    x: [0, 90 * r(1), -50 * r(2), 130 * r(3), -100 * r(4), 70 * r(5), 0],
    y: [0, -130 * r(6) - 30, 80 * r(7), -140 * r(8) - 20, 100 * r(9), -60 * r(10), 0],
    r: [0, 14 * r(11), -10 * r(12), 18 * r(13), -8 * r(14), 12 * r(15), 0],
    opacity: [0.85, 1, 0.95, 1, 0.92, 1, 0.85],
    times: [0, 0.18, 0.4, 0.58, 0.75, 0.9, 1],
  };
}

// Parallax factor by size — smaller birds appear distant, drift slower vs scroll
// Positive value means bird "lags behind" scroll (translateY counteracts scroll-up)
function parallaxForSize(size) {
  if (size <= 30) return 0.30;
  if (size <= 34) return 0.18;
  if (size <= 38) return 0.08;
  return 0;
}

// 18 birds with varied size, blue variant, direction, loop behavior and flap phase
// flapPhase: ms offset (0..flap-1) so each bird's wing-beat is at a different phase
// Distributed from 8vh to ~680vh to cover the entire document
const SPECS = [
  { topVh: 8,   leftPct: 12, size: 36, dur: 22, delay: 0,    flap: 380, flapPhase: 0,   tilt: -4,  dir: 1,  loop: false, variant: 0 },
  { topVh: 22,  leftPct: 78, size: 30, dur: 26, delay: 1.5,  flap: 420, flapPhase: 80,  tilt: 6,   dir: -1, loop: true,  variant: 1 },
  { topVh: 55,  leftPct: 28, size: 38, dur: 28, delay: 3,    flap: 360, flapPhase: 150, tilt: -8,  dir: 1,  loop: false, variant: 2 },
  { topVh: 80,  leftPct: 65, size: 32, dur: 30, delay: 0.8,  flap: 400, flapPhase: 220, tilt: 4,   dir: -1, loop: true,  variant: 3 },
  { topVh: 115, leftPct: 18, size: 36, dur: 27, delay: 2.2,  flap: 390, flapPhase: 60,  tilt: -10, dir: 1,  loop: false, variant: 4 },
  { topVh: 150, leftPct: 72, size: 30, dur: 32, delay: 4,    flap: 410, flapPhase: 270, tilt: 6,   dir: -1, loop: true,  variant: 5 },
  { topVh: 195, leftPct: 35, size: 34, dur: 30, delay: 1,    flap: 370, flapPhase: 110, tilt: -4,  dir: 1,  loop: false, variant: 1 },
  { topVh: 235, leftPct: 82, size: 28, dur: 26, delay: 3.5,  flap: 430, flapPhase: 200, tilt: 8,   dir: -1, loop: false, variant: 4 },
  { topVh: 280, leftPct: 22, size: 38, dur: 34, delay: 0.5,  flap: 380, flapPhase: 50,  tilt: -6,  dir: 1,  loop: true,  variant: 0 },
  { topVh: 320, leftPct: 60, size: 32, dur: 28, delay: 2.8,  flap: 400, flapPhase: 290, tilt: 4,   dir: -1, loop: false, variant: 2 },
  { topVh: 360, leftPct: 38, size: 30, dur: 30, delay: 1.7,  flap: 410, flapPhase: 130, tilt: -8,  dir: 1,  loop: true,  variant: 5 },
  { topVh: 400, leftPct: 75, size: 36, dur: 27, delay: 3.2,  flap: 370, flapPhase: 240, tilt: 6,   dir: -1, loop: false, variant: 3 },
  { topVh: 445, leftPct: 25, size: 32, dur: 28, delay: 0.6,  flap: 395, flapPhase: 90,  tilt: -6,  dir: 1,  loop: true,  variant: 1 },
  { topVh: 490, leftPct: 70, size: 36, dur: 30, delay: 2.4,  flap: 380, flapPhase: 180, tilt: 4,   dir: -1, loop: false, variant: 4 },
  { topVh: 540, leftPct: 40, size: 30, dur: 32, delay: 1.1,  flap: 415, flapPhase: 30,  tilt: -10, dir: 1,  loop: false, variant: 2 },
  { topVh: 585, leftPct: 80, size: 38, dur: 26, delay: 3.8,  flap: 360, flapPhase: 250, tilt: 8,   dir: -1, loop: true,  variant: 0 },
  { topVh: 625, leftPct: 18, size: 28, dur: 29, delay: 1.4,  flap: 425, flapPhase: 140, tilt: -4,  dir: 1,  loop: false, variant: 5 },
  { topVh: 670, leftPct: 55, size: 34, dur: 30, delay: 2.6,  flap: 390, flapPhase: 70,  tilt: 6,   dir: -1, loop: true,  variant: 3 },
];

export default function Butterflies() {
  const { scrollY } = useScroll();

  useEffect(() => {
    let timeoutId;
    const tryPlay = () => {
      try {
        playTweet();
      } catch (err) {
        // Web Audio may be blocked / unavailable — non-fatal
        console.debug("[Birds] tweet sound failed:", err);
      }
    };
    const scheduleNext = () => {
      const delay = 18000 + Math.random() * 27000;
      timeoutId = setTimeout(() => {
        tryPlay();
        scheduleNext();
      }, delay);
    };
    timeoutId = setTimeout(() => {
      tryPlay();
      scheduleNext();
    }, 5000 + Math.random() * 4000);
    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <>
      <style>{`
        @keyframes bird-show-open {
          0%, 49.99%   { opacity: 1; }
          50%, 100%    { opacity: 0; }
        }
        @keyframes bird-show-closed {
          0%, 49.99%   { opacity: 0; }
          50%, 100%    { opacity: 1; }
        }
      `}</style>
      <div
        aria-hidden
        className="absolute inset-0 z-[30] pointer-events-none overflow-hidden"
        data-testid="birds-overlay"
      >
        {SPECS.map((s, i) => (
          <Bird
            key={`bird-${s.topVh}-${s.leftPct}`}
            size={s.size}
            baseTopVh={s.topVh}
            baseLeftPct={s.leftPct}
            range={s.loop ? makeLoopRange(i + 1) : makeRange(i + 1)}
            duration={s.dur}
            delay={s.delay}
            flapSpeed={s.flap}
            flapPhase={s.flapPhase}
            bodyTilt={s.tilt}
            direction={s.dir}
            variant={s.variant}
            parallaxFactor={parallaxForSize(s.size)}
            scrollY={scrollY}
          />
        ))}
      </div>
    </>
  );
}
